import React from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowRight, ArrowUpRight } from "lucide-react";
import { avgScore, slugify, GUIDES } from "../mock";
import { usePlaces } from "../context/PlacesContext";
import { PlaceCard, SectionLabel, VerdictBadge } from "../components/Bits";

export default function City() {
  const { slug } = useParams();
  const { places, placesFromSlugs, loading } = usePlaces();

  if (loading) {
    return <div className="pt-[140px] pb-40 text-center text-ink-soft font-serif text-2xl">Loading…</div>;
  }

  const cityPlaces = places.filter((p) => slugify(p.city) === slug);
  const first = cityPlaces[0];

  if (!first) {
    return (
      <div className="pt-[140px] pb-40 text-center">
        <h1 className="font-serif text-4xl">City not found.</h1>
        <Link to="/destinations" className="inline-block mt-6 text-claret link-underline">Browse destinations</Link>
      </div>
    );
  }

  const cityName = first.city;
  const region = first.region;
  const country = first.country;
  const sorted = [...cityPlaces].sort((a, b) => avgScore(b.scores) - avgScore(a.scores));
  const hero = sorted[0];
  const featured = sorted.slice(0, 3);
  const avg = Math.round((cityPlaces.reduce((s, p) => s + avgScore(p.scores), 0) / cityPlaces.length) * 10) / 10;
  const neighbourhoods = [...new Set(cityPlaces.map((p) => p.neighborhood))];
  const localGuide = GUIDES.find((g) => slugify(g.city || "") === slug && placesFromSlugs(g.placeSlugs).length > 0);
  const mapQuery = `${cityName}, ${region}, ${country}`;

  return (
    <div className="pt-[68px]">
      {/* Hero */}
      <section className="relative h-[62vh] min-h-[440px] flex items-end overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={hero.image} alt={cityName} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="absolute inset-0 grain" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full pb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-brass" />
            <Link to={`/explore?country=${encodeURIComponent(country)}&region=${encodeURIComponent(region)}`} className="eyebrow text-brass hover:underline">{region} · {country}</Link>
          </div>
          <h1 className="font-serif text-paper text-5xl sm:text-6xl md:text-8xl font-semibold leading-none">{cityName}</h1>
          <div className="flex items-center gap-6 mt-6 text-paper/80 text-sm">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {region}, {country}</span>
            <span>{cityPlaces.length} reviewed {cityPlaces.length === 1 ? "place" : "places"}</span>
            <span>{cityPlaces.filter((p) => (p.distinction || 0) > 0).length} with distinction</span>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
        <SectionLabel>The city in brief</SectionLabel>
        <p className="font-serif text-2xl sm:text-3xl md:text-4xl leading-snug mt-6 display-balance">
          {cityName} is one of {country}'s most rewarding cities to eat and drink in — and our reviewers
          keep finding new reasons to return.
        </p>
      </section>

      {/* Featured */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-8">
        <div className="flex items-end justify-between mb-10">
          <div>
            <SectionLabel>Featured places</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-4">Where to start.</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {featured.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
        </div>
      </section>

      {/* Neighbourhoods + map */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
          <div>
            <SectionLabel>Neighbourhoods</SectionLabel>
            <div className="flex flex-wrap gap-2 mt-6">
              {neighbourhoods.map((n) => (
                <Link key={n} to={`/neighborhood/${slugify(n)}`} className="px-4 py-2 bg-ink/5 hover:bg-claret hover:text-paper text-sm font-medium transition-colors">{n}</Link>
              ))}
            </div>
            {localGuide && (
              <Link to={`/guide/${localGuide.slug}`} className="group block mt-10 border border-ink/12 hover:border-claret/40 transition-colors">
                <div className="img-zoom relative aspect-[3/1]">
                  <img src={localGuide.cover} alt={localGuide.title} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                  <span className="absolute bottom-3 left-4 eyebrow text-brass text-[10px]">Local guide</span>
                </div>
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <h3 className="font-serif text-2xl font-semibold group-hover:text-claret transition-colors">{localGuide.title}</h3>
                    <p className="text-sm text-ink-soft mt-1 line-clamp-1">{localGuide.dek}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-ink-soft group-hover:text-claret transition-colors shrink-0" />
                </div>
              </Link>
            )}
          </div>
          <div className="lg:sticky lg:top-24">
            <div className="border border-ink/12 overflow-hidden bg-paper">
              <iframe title={`${cityName} map`} src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=12&output=embed`} className="w-full h-[460px] grayscale-[0.15]" loading="lazy" style={{ border: 0 }} />
            </div>
          </div>
        </div>
      </section>

      {/* All places */}
      <section className="bg-paper-dark relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 relative">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold">Every reviewed place in {cityName}</h2>
            <Link to={`/explore?country=${encodeURIComponent(country)}&region=${encodeURIComponent(region)}&city=${encodeURIComponent(cityName)}`} className="hidden md:inline-flex items-center gap-2 text-sm font-semibold link-underline">
              Open in Explore <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
            {sorted.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
